// Theme presets for the slot UI shell.
// Consumers can pick a preset by key (`applyTheme('mint')`) or merge in custom CSS
// variable values via `applyTheme({ vars: { '--accent': '#FF00AA' } })`.

export type ThemeVars = {
  '--bg': string;
  '--surface': string;
  '--surface-2': string;
  '--surface-3': string;
  '--border': string;
  '--border-2': string;
  '--text': string;
  '--text-muted': string;
  '--text-dim': string;
  '--accent': string;
  '--accent-soft': string;
  '--accent-ink': string;
  '--reward': string;
  '--danger': string;
  '--cell': string;
  '--cell-border': string;
  '--shadow': string;
  '--shadow-sm': string;
};

export interface Theme {
  name: string;
  swatch: [string, string, string];
  vars: ThemeVars;
}

export const THEMES: Record<string, Theme> = {
  'dusk-teal': {
    name: 'Dusk Teal',
    swatch: ['#1B2428', '#3FB5B5', '#C7D84A'],
    vars: {
      '--bg': '#0F1619',
      '--surface': '#1B2428',
      '--surface-2': '#26323A',
      '--surface-3': '#3A4850',
      '--border': 'rgba(255,255,255,0.06)',
      '--border-2': 'rgba(255,255,255,0.1)',
      '--text': '#F5F2ED',
      '--text-muted': '#8A96A0',
      '--text-dim': '#6B7680',
      '--accent': '#3FB5B5',
      '--accent-soft': 'rgba(63,181,181,0.14)',
      '--accent-ink': '#0F1619',
      '--reward': '#C7D84A',
      '--danger': '#D9534F',
      '--cell': '#161F23',
      '--cell-border': 'rgba(255,255,255,0.05)',
      '--shadow': '0 8px 32px rgba(0,0,0,0.4)',
      '--shadow-sm': '0 2px 8px rgba(0,0,0,0.3)',
    },
  },
  midnight: {
    name: 'Midnight',
    swatch: ['#0B0F1A', '#6B6BFF', '#F0B84A'],
    vars: {
      '--bg': '#070914',
      '--surface': '#0B0F1A',
      '--surface-2': '#141A2B',
      '--surface-3': '#222A43',
      '--border': 'rgba(255,255,255,0.06)',
      '--border-2': 'rgba(255,255,255,0.1)',
      '--text': '#EDEEF5',
      '--text-muted': '#8A90A8',
      '--text-dim': '#5F6482',
      '--accent': '#6B6BFF',
      '--accent-soft': 'rgba(107,107,255,0.16)',
      '--accent-ink': '#070914',
      '--reward': '#F0B84A',
      '--danger': '#FF5E5B',
      '--cell': '#0E1322',
      '--cell-border': 'rgba(255,255,255,0.05)',
      '--shadow': '0 8px 32px rgba(0,0,0,0.5)',
      '--shadow-sm': '0 2px 8px rgba(0,0,0,0.35)',
    },
  },
  sunset: {
    name: 'Sunset',
    swatch: ['#231822', '#FF6B6B', '#FFD166'],
    vars: {
      '--bg': '#1A1016',
      '--surface': '#231822',
      '--surface-2': '#2E2029',
      '--surface-3': '#3F2C38',
      '--border': 'rgba(255,255,255,0.06)',
      '--border-2': 'rgba(255,255,255,0.1)',
      '--text': '#F8F0EA',
      '--text-muted': '#A08F97',
      '--text-dim': '#776269',
      '--accent': '#FF6B6B',
      '--accent-soft': 'rgba(255,107,107,0.16)',
      '--accent-ink': '#1A1016',
      '--reward': '#FFD166',
      '--danger': '#E53E3E',
      '--cell': '#1E141A',
      '--cell-border': 'rgba(255,255,255,0.05)',
      '--shadow': '0 8px 32px rgba(0,0,0,0.45)',
      '--shadow-sm': '0 2px 8px rgba(0,0,0,0.3)',
    },
  },
  reelay: {
    name: 'Reelay Pink',
    swatch: ['#090418', '#FE37B4', '#FFFFFF'],
    vars: {
      '--bg': '#090418',
      '--surface': '#130B2E',
      '--surface-2': '#1C1240',
      '--surface-3': '#271A52',
      '--border': 'rgba(255,255,255,0.07)',
      '--border-2': 'rgba(255,255,255,0.12)',
      '--text': '#FFFFFF',
      '--text-muted': '#B8A8D0',
      '--text-dim': '#7B6A9A',
      '--accent': '#FE37B4',
      '--accent-soft': 'rgba(254,55,180,0.16)',
      '--accent-ink': '#FFFFFF',
      '--reward': '#FFD166',
      '--danger': '#FF5E5B',
      '--cell': '#0E0824',
      '--cell-border': 'rgba(255,255,255,0.05)',
      '--shadow': '0 8px 32px rgba(0,0,0,0.5)',
      '--shadow-sm': '0 2px 8px rgba(0,0,0,0.35)',
    },
  },
  mint: {
    name: 'Mint Light',
    swatch: ['#F5F7F4', '#1A8F6B', '#2B2D34'],
    vars: {
      '--bg': '#ECEFEA',
      '--surface': '#F5F7F4',
      '--surface-2': '#FFFFFF',
      '--surface-3': '#E5E9E3',
      '--border': 'rgba(0,0,0,0.07)',
      '--border-2': 'rgba(0,0,0,0.12)',
      '--text': '#1A1F1C',
      '--text-muted': '#5B6560',
      '--text-dim': '#8A938D',
      '--accent': '#1A8F6B',
      '--accent-soft': 'rgba(26,143,107,0.12)',
      '--accent-ink': '#FFFFFF',
      '--reward': '#E07A1F',
      '--danger': '#C73636',
      '--cell': '#E9EDE7',
      '--cell-border': 'rgba(0,0,0,0.06)',
      '--shadow': '0 8px 32px rgba(0,0,0,0.08)',
      '--shadow-sm': '0 2px 8px rgba(0,0,0,0.06)',
    },
  },
};

export type ThemeKey = keyof typeof THEMES;

export const THEME_KEYS = Object.keys(THEMES) as ThemeKey[];

export type ThemeInput = ThemeKey | Theme | { vars: Partial<ThemeVars> };

/**
 * Apply a theme to the document root by setting CSS custom properties.
 * Accepts a theme key, a full Theme object, or a partial `{ vars }` patch.
 */
export function applyTheme(
  input: ThemeInput,
  root: HTMLElement = document.documentElement,
): void {
  let theme: Theme | { vars: Partial<ThemeVars> };
  let themeKey: string | undefined;

  if (typeof input === 'string') {
    theme = THEMES[input] ?? THEMES['dusk-teal'];
    themeKey = input;
  } else {
    theme = input;
  }

  Object.entries(theme.vars).forEach(([k, v]) => {
    if (typeof v === 'string') root.style.setProperty(k, v);
  });

  if (themeKey) root.setAttribute('data-theme', themeKey);
  if (typeof document !== 'undefined' && 'vars' in theme && theme.vars['--bg']) {
    document.body.style.background = theme.vars['--bg'] as string;
  }
}
